import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, ExternalLink, Star, Edit3, Play, Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ChampionTrailer from "@/components/ChampionTrailer";

export default function Trophies() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [champs, setChamps] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [editFor, setEditFor] = useState(null);
  const [draft, setDraft] = useState({ highlight_url: "", ring_note: "", mvp_player_id: "", tournament: "" });
  const [previewFor, setPreviewFor] = useState(null);
  const [rulings, setRulings] = useState([]);

  const load = () => {
    api.get("/champions").then((r) => setChamps(r.data));
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/rulings/audit").then((r) => setRulings(r.data)).catch(() => setRulings([]));
  };
  useEffect(() => { load(); }, []);

  const teamById = (id) => teams.find((t) => t.id === id);
  const playerById = (id) => players.find((p) => p.id === id);

  const openEditor = (c) => {
    setEditFor(c);
    setDraft({
      highlight_url: c.highlight_url || "",
      ring_note: c.ring_note || "",
      mvp_player_id: c.mvp_player_id || "",
      tournament: c.tournament || "",
    });
  };

  const saveTrailer = async () => {
    try {
      const body = {};
      if (draft.highlight_url !== (editFor.highlight_url || "")) body.highlight_url = draft.highlight_url;
      if (draft.ring_note !== (editFor.ring_note || "")) body.ring_note = draft.ring_note;
      if (draft.mvp_player_id !== (editFor.mvp_player_id || "")) body.mvp_player_id = draft.mvp_player_id || null;
      if (draft.tournament !== (editFor.tournament || "")) body.tournament = draft.tournament;
      await api.put(`/admin/champions/${editFor.id}`, body);
      toast.success("Trailer updated.");
      setEditFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-10" data-testid="trophies-root">
      <div>
        <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-2">/ HALL OF CHAMPIONS</div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Trophy Room</h1>
        <p className="text-gray-400 mt-2 max-w-xl">
          Every ring, every ratings sheet, every highlight reel. Legacy remembers.
        </p>
      </div>

      <div className="space-y-6">
        {champs.map((c, idx) => {
          const w = teamById(c.winner_team_id);
          const r = teamById(c.runner_up_team_id);
          const mvp = playerById(c.mvp_player_id);
          const featured = idx === 0;
          return (
            <div
              key={c.id}
              data-testid={`trophy-${c.season}`}
              className={`grid lg:grid-cols-[240px_1fr] gap-6 border ${featured ? "border-[#F4D03F]" : "border-[#232330]"} bg-[#101015] p-6`}
            >
              {/* Ring */}
              <div className="flex flex-col items-center justify-center">
                <div
                  className={`relative w-40 h-40 rounded-full border-8 ${featured ? "border-[#F4D03F]" : "border-[#232330]"} flex items-center justify-center`}
                  style={{ background: `radial-gradient(circle at 30% 30%, ${w?.logo_color}44, #05050A 70%)` }}
                >
                  <Trophy className={`w-14 h-14 ${featured ? "text-[#F4D03F]" : "text-gray-400"}`} strokeWidth={1.5} />
                </div>
                <div className="mt-4 text-center">
                  <div className="font-heading font-black text-2xl uppercase tracking-tighter">{c.season}</div>
                  <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase">{c.year} · {c.tournament}</div>
                </div>
              </div>

              {/* Body */}
              <div className="space-y-4">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[10px] font-mono tracking-widest text-[#F4D03F] uppercase">Champion</span>
                    <span className="font-heading font-black text-3xl uppercase tracking-tight" style={{ color: w?.logo_color }}>
                      {w?.name}
                    </span>
                    {r && (
                      <span className="text-xs text-gray-500 font-mono">
                        def. <span className="text-gray-300">{r?.name}</span>
                      </span>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      onClick={() => openEditor(c)}
                      data-testid={`edit-trailer-${c.season}`}
                      className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-[10px] font-bold"
                    >
                      <Edit3 className="w-3 h-3 mr-1" /> Edit trailer
                    </Button>
                  )}
                </div>

                <p className="text-gray-300 leading-relaxed">{c.ring_note}</p>

                <div className="grid sm:grid-cols-3 gap-3 pt-3">
                  <div className="border border-[#232330] p-3">
                    <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase">Final MVP</div>
                    <div className="mt-1 flex items-center gap-2">
                      <Star className="w-3.5 h-3.5 text-[#F4D03F]" />
                      <span className="font-heading font-bold uppercase tracking-tight text-sm">{mvp?.handle || "TBD"}</span>
                    </div>
                    {mvp && <div className="text-[10px] text-gray-500 mt-1 font-mono">RTG {(mvp.rating ?? 0).toFixed(2)}</div>}
                  </div>
                  <div className="border border-[#232330] p-3">
                    <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase">Champion Rating</div>
                    <div className="mt-1 font-heading font-black text-xl text-cyan-400">
                      {w ? ((w.wins || 0) * 3 + (w.draws || 0)).toFixed(0) : "—"}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1 font-mono">form points</div>
                  </div>
                  {c.highlight_url ? (
                    <button
                      onClick={() => setPreviewFor(c)}
                      data-testid={`trophy-play-${c.season}`}
                      className="border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black transition-colors p-3 flex items-center justify-between"
                    >
                      <span className="font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                        <Play className="w-3.5 h-3.5" /> Play trailer
                      </span>
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  ) : (
                    <div className="border border-dashed border-[#232330] p-3 text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center justify-center">
                      Trailer pending
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {champs.length === 0 && (
          <div className="border border-[#232330] p-8 text-center text-gray-500">
            No champions yet — the first season is still writing itself.
          </div>
        )}
      </div>

      {/* Iter34: Ruling Audit Log — public transparency on how disputes get resolved */}
      <section className="space-y-4" data-testid="rulings-audit-section">
        <div className="flex items-baseline gap-3 flex-wrap">
          <Gavel className="w-5 h-5 text-[#F4D03F]" />
          <h2 className="font-heading font-black text-2xl lg:text-3xl uppercase tracking-tighter">Officials · Ruling Audit</h2>
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
            {rulings.length} ruling{rulings.length === 1 ? "" : "s"} on the record
          </span>
        </div>
        <p className="text-xs text-gray-500 font-mono uppercase tracking-widest">
          Every official ruling that ever locked a disputed fixture is public — accountability by default.
        </p>
        {rulings.length === 0 ? (
          <div className="border border-[#232330] p-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            No rulings on the record yet — every score locked cleanly by rep signature.
          </div>
        ) : (
          <div className="grid gap-2">
            {rulings.map((r) => (
              <div
                key={r.id}
                data-testid={`ruling-${r.id}`}
                className="border border-[#232330] bg-[#101015] p-4 grid gap-2 md:grid-cols-12 items-center hover:border-[#F4D03F]/60 transition-colors"
              >
                <div className="md:col-span-4 min-w-0">
                  <div className="font-heading font-black uppercase tracking-tight truncate">
                    <span style={{ color: r.home_color }}>{r.home_tag || "TBD"}</span>
                    <span className="text-gray-500 mx-2">vs</span>
                    <span style={{ color: r.away_color }}>{r.away_tag || "TBD"}</span>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1 truncate">
                    {r.title || "Dispute"}
                  </div>
                </div>
                <div className="md:col-span-2 text-center">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Ruled</div>
                  <div className="font-heading font-black text-2xl mt-0.5 text-[#F4D03F]" data-testid={`ruling-score-${r.id}`}>
                    {r.ruled_final_home}–{r.ruled_final_away}
                  </div>
                </div>
                <div className="md:col-span-4 text-xs text-gray-300 italic line-clamp-2">
                  {r.response || "—"}
                </div>
                <div className="md:col-span-2 text-right">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 truncate">
                    {r.ruled_by || "official"}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                    {r.ruled_at ? new Date(r.ruled_at).toLocaleDateString() : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Preview modal — reuse ChampionTrailer with pre-open state */}
      {previewFor && (
        <div
          data-testid="trophy-trailer-preview"
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto"
          onClick={() => setPreviewFor(null)}
        >
          <div className="max-w-4xl mx-auto" onClick={(e) => e.stopPropagation()}>
            <ChampionTrailer
              team={teamById(previewFor.winner_team_id)}
              highlightUrl={previewFor.highlight_url}
              ringNote={previewFor.ring_note}
              mvp={playerById(previewFor.mvp_player_id)}
              season={previewFor.season}
              year={previewFor.year}
            />
            <button
              onClick={() => setPreviewFor(null)}
              className="mt-4 text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-white"
            >
              ← Close
            </button>
          </div>
        </div>
      )}

      {/* Trailer editor */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Trailer editor · {editFor?.season} {editFor?.year}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Highlight URL (YouTube or Twitch)</Label>
              <Input
                data-testid="trailer-highlight-url"
                value={draft.highlight_url}
                onChange={(e) => setDraft({ ...draft, highlight_url: e.target.value })}
                placeholder="https://youtu.be/... or https://twitch.tv/videos/..."
                className="rounded-none bg-[#05050A] border-[#232330] font-mono text-sm mt-2"
              />
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                Plays inside a modal on Bracket & Trophy pages.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Ring note</Label>
              <Textarea
                data-testid="trailer-ring-note"
                rows={3}
                value={draft.ring_note}
                onChange={(e) => setDraft({ ...draft, ring_note: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2"
                placeholder="How the ring was won — a headline, a moment, a stat."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Final MVP</Label>
                <Select value={draft.mvp_player_id || "__none__"} onValueChange={(v) => setDraft({ ...draft, mvp_player_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="trailer-mvp" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                    <SelectValue placeholder="Pick MVP" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#101015] border-[#232330] text-white max-h-72">
                    <SelectItem value="__none__">— none —</SelectItem>
                    {players.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.handle}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Tournament</Label>
                <Input
                  data-testid="trailer-tournament"
                  value={draft.tournament}
                  onChange={(e) => setDraft({ ...draft, tournament: e.target.value })}
                  className="rounded-none bg-[#05050A] border-[#232330] mt-2"
                />
              </div>
            </div>
            <Button
              onClick={saveTrailer}
              data-testid="trailer-save"
              className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold mt-2"
            >
              Ship trailer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
